import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../components/Icon';
import MultiSelect from '../components/MultiSelect';
import CustomDatePicker from '../components/CustomDatePicker';
import PageSizeDropdown from '../components/PageSizeDropdown';
import { searchBalanceTransactions } from '../api';
import { formatDisplayDate } from '../utils/promoCodeUtils';
import { pushDetailNavState } from '../utils/detailNavState';
import '../css/pagination.css';

// Значения enum BalanceTransactionType на бэкенде + направление (CREDIT/DEBIT)
// для бейджа — совпадает с backend-энамом BalanceTransactionType.Direction.
const TRANSACTION_TYPES = [
  { value: 'TOPUP_MODERATOR', direction: 'CREDIT' },
  { value: 'TOPUP_PACKAGE_PURCHASE', direction: 'CREDIT' },
  { value: 'TOPUP_PACKAGE_PURCHASE_MIA', direction: 'CREDIT' },
  { value: 'FIRST_PACKAGE_BONUS', direction: 'CREDIT' },
  { value: 'REFERRAL_BONUS', direction: 'CREDIT' },
  { value: 'WITHDRAWAL_MODERATOR', direction: 'DEBIT' },
  { value: 'ORDER_PARTICIPATION', direction: 'DEBIT' },
  { value: 'ORDER_PAYMENT', direction: 'DEBIT' },
];

const directionOf = (type) => TRANSACTION_TYPES.find((t) => t.value === type)?.direction || 'CREDIT';

const DEFAULT_FILTERS = { userProfileId: '', orderId: '', types: [], createdFrom: '', createdTo: '' };

// Фильтры могут приходить из сохранённого nav-state (в т.ч. старого формата с одиночным `type`).
const normalizeFilters = (raw) => {
  if (!raw) return DEFAULT_FILTERS;
  const types = Array.isArray(raw.types) ? raw.types : (raw.type ? [raw.type] : []);
  return {
    userProfileId: raw.userProfileId || '',
    orderId: raw.orderId || '',
    types,
    createdFrom: raw.createdFrom || '',
    createdTo: raw.createdTo || '',
  };
};

const formatAmount = (value, locale) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const AdminTransactions = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'ru';
  const navigate = useNavigate();
  const location = useLocation();

  const goToUser = (userId) => {
    if (!userId) return;
    navigate(`/users/${userId}`, {
      state: pushDetailNavState(location, {
        fromLogs: { path: location.pathname, tab: 'transactions', page, size: pageSize, filters: appliedFilters },
      }),
    });
  };

  const goToOrder = (orderId) => {
    if (!orderId) return;
    navigate(`/orders/${orderId}`, {
      state: pushDetailNavState(location, {
        fromLogs: { path: location.pathname, tab: 'transactions', page, size: pageSize, filters: appliedFilters },
      }),
    });
  };

  const typeLabel = useCallback(
    (type) => t(`admin.transactions.types.${type}`, type),
    [t]
  );

  const typeOptions = React.useMemo(
    () =>
      TRANSACTION_TYPES.map(({ value, direction }) => ({
        value,
        label: typeLabel(value),
        icon: direction === 'CREDIT' ? 'trending-up' : 'trending-down',
      })),
    [typeLabel]
  );

  const restoredNavState = location.state?.fromLogs?.tab === 'transactions' ? location.state.fromLogs : null;

  const [filters, setFilters] = useState(() => normalizeFilters(restoredNavState?.filters));
  const [appliedFilters, setAppliedFilters] = useState(() => normalizeFilters(restoredNavState?.filters));

  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(() => restoredNavState?.page || 0);
  const [pageSize, setPageSize] = useState(() => restoredNavState?.size || 20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(async (nextFilters, nextPage, nextSize) => {
    setLoading(true);
    setError(null);
    try {
      const data = await searchBalanceTransactions(nextFilters, nextPage, nextSize, 'createdAt', 'DESC');
      setTransactions(Array.isArray(data?.content) ? data.content : []);
      setSummary(data?.summary || null);
      setTotalPages(Number(data?.totalPages) || 0);
      setTotalElements(Number(data?.totalElements) || 0);
    } catch (e) {
      setError(e?.message || tRef.current('admin.transactions.loadError'));
      setTransactions([]);
      setSummary(null);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка запускается автоматически при любом изменении применённых фильтров/страницы/размера страницы
  useEffect(() => {
    load(appliedFilters, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, page, pageSize]);

  // Debounce текстовых полей поиска: применяем их в appliedFilters с задержкой
  // (сбрасываем страницу только если значения реально изменились — иначе слетала бы восстановленная страница)
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedUser = filters.userProfileId.trim();
      const trimmedOrder = filters.orderId.trim();
      setAppliedFilters((prev) => {
        if (prev.userProfileId === trimmedUser && prev.orderId === trimmedOrder) return prev;
        setPage(0);
        return { ...prev, userProfileId: trimmedUser, orderId: trimmedOrder };
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.userProfileId, filters.orderId]);

  const handleTypesChange = (nextTypes) => {
    setFilters((prev) => ({ ...prev, types: nextTypes }));
    setAppliedFilters((prev) => ({ ...prev, types: nextTypes }));
    setPage(0);
  };

  // Границы периода: храним ISO-строку (ZonedDateTime на бэкенде)
  const handleDateChange = (key) => (date) => {
    const value = date ? date.toISOString() : '';
    setFilters((prev) => ({ ...prev, [key]: value }));
    setAppliedFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleApply = (e) => {
    e.preventDefault();
    setAppliedFilters({
      ...filters,
      userProfileId: filters.userProfileId.trim(),
      orderId: filters.orderId.trim(),
    });
    setPage(0);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (size) => {
    const nextSize = Number(size) || 20;
    setPageSize(nextSize);
    setPage(0);
  };

  const filtersActive =
    appliedFilters.userProfileId !== '' ||
    appliedFilters.orderId !== '' ||
    appliedFilters.types.length > 0 ||
    appliedFilters.createdFrom !== '' ||
    appliedFilters.createdTo !== '';

  const getVisiblePages = () => {
    const total = totalPages || 0;
    const current = page;
    if (total <= 1) return [];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);

    const pages = [0];
    const left = Math.max(1, current - 1);
    const right = Math.min(total - 2, current + 1);
    if (left > 1) pages.push('left-ellipsis');
    for (let p = left; p <= right; p += 1) pages.push(p);
    if (right < total - 2) pages.push('right-ellipsis');
    pages.push(total - 1);
    return pages;
  };

  // Итоги по текущей выборке (приходят рядом со страницей транзакций)
  const renderSummary = () => {
    if (!summary) return null;

    const byType = Object.entries(summary.byType || {}).filter(([, amount]) => Number(amount) !== 0);
    const net = Number(summary.net) || 0;

    return (
      <div className="orders-content-surface admin-tx-summary" style={{ marginBottom: '24px' }}>
        <div className="admin-tx-summary-cards">
          <div className="admin-tx-summary-card admin-tx-summary-card--credit">
            <span className="admin-tx-summary-label">
              <Icon name="trending-up" size={14} />
              {t('admin.transactions.summary.totalCredit')}
            </span>
            <span className="admin-tx-summary-value">
              {t('admin.transactions.amountValue', { amount: formatAmount(summary.totalCredit, locale) })}
            </span>
          </div>
          <div className="admin-tx-summary-card admin-tx-summary-card--debit">
            <span className="admin-tx-summary-label">
              <Icon name="trending-down" size={14} />
              {t('admin.transactions.summary.totalDebit')}
            </span>
            <span className="admin-tx-summary-value">
              {t('admin.transactions.amountValue', { amount: formatAmount(summary.totalDebit, locale) })}
            </span>
          </div>
          <div className={`admin-tx-summary-card ${net < 0 ? 'admin-tx-summary-card--debit' : 'admin-tx-summary-card--credit'}`}>
            <span className="admin-tx-summary-label">
              <Icon name="credit-card" size={14} />
              {t('admin.transactions.summary.net')}
            </span>
            <span className="admin-tx-summary-value">
              {t('admin.transactions.amountValue', { amount: formatAmount(net, locale) })}
            </span>
          </div>
        </div>

        {byType.length > 0 && (
          <div className="admin-tx-summary-bytype">
            <span className="admin-tx-summary-bytype-title">{t('admin.transactions.summary.byType')}</span>
            <div className="admin-tx-summary-chips">
              {byType.map(([type, amount]) => (
                <span key={type} className="admin-tx-summary-chip">
                  <Icon name={directionOf(type) === 'CREDIT' ? 'trending-up' : 'trending-down'} size={12} />
                  <span className="admin-tx-summary-chip-label">{typeLabel(type)}</span>
                  <span className="admin-tx-summary-chip-value">
                    {t('admin.transactions.amountValue', { amount: formatAmount(amount, locale) })}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPager = () => {
    if ((loading && transactions.length === 0) || totalPages <= 0) return null;

    const isFirst = page <= 0;
    const isLast = page >= totalPages - 1;
    const pages = getVisiblePages();

    const total = totalElements || 0;
    const from = total > 0 ? page * pageSize + 1 : 0;
    const to = Math.min((page + 1) * pageSize, total);

    return (
      <div className="orders-pager orders-pager-bottom">
        <div className="orders-pager-info">
          {t('common.pagination.showing', { from, to, total })}
        </div>

        <div className="orders-pager-controls">
          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handlePageChange(page - 1)}
            disabled={isFirst || loading}
          >
            {t('common.pagination.previous')}
          </button>

          <div className="orders-pager-pages">
            {pages.map((item) => {
              if (typeof item !== 'number') {
                return <span key={item} className="orders-pager-ellipsis">...</span>;
              }
              const active = item === page;
              return (
                <button
                  key={item}
                  type="button"
                  className={`orders-pager-page ${active ? 'active' : ''}`}
                  onClick={() => handlePageChange(item)}
                  disabled={loading}
                  aria-current={active ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handlePageChange(page + 1)}
            disabled={isLast || loading}
          >
            {t('common.pagination.next')}
          </button>

          <PageSizeDropdown
            value={pageSize}
            onChange={handlePageSizeChange}
            options={[10, 20, 50, 100]}
            position="bottom"
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="orders-content-surface" style={{ marginBottom: '24px' }}>
        <div className="orders-controls-surface" style={{ marginBottom: 0 }}>
          <div className="list-summary-row">
            <div className="list-summary-header">
              <div className="list-summary-header-left">
                <div className="list-summary-icon">
                  <Icon name="credit-card" size={24} />
                </div>
                <div>
                  <h3 className="list-summary-title">{t('admin.transactions.title')}</h3>
                  <p className="list-summary-subtitle">
                    {t('admin.transactions.totalCount', { count: totalElements })}
                  </p>
                </div>
              </div>
            </div>
            <div className="order-logs-export-wrap">
              <button
                type="button"
                className="btn btn-secondary order-logs-export-button"
                onClick={() => load(appliedFilters, page, pageSize)}
                disabled={loading}
                title={t('admin.transactions.refresh')}
              >
                <Icon name="refresh" size={18} />
                {t('admin.transactions.refresh')}
              </button>
            </div>
          </div>

          {/* Базовый поиск по фильтрам: пользователь, заказ, тип операции */}
          <form className="search-panel" onSubmit={handleApply}>
            <div className="search-form">
              <div className="search-secondary-filters">
                <div className="filter-group">
                  <label className="filter-label" htmlFor="tx-filter-user">
                    {t('admin.transactions.filters.userProfileId')}
                  </label>
                  <input
                    id="tx-filter-user"
                    type="text"
                    className="filter-input"
                    value={filters.userProfileId}
                    onChange={(e) => setFilters((prev) => ({ ...prev, userProfileId: e.target.value }))}
                    placeholder={t('admin.transactions.filters.userProfileIdPlaceholder')}
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label" htmlFor="tx-filter-order">
                    {t('admin.transactions.filters.orderId')}
                  </label>
                  <input
                    id="tx-filter-order"
                    type="text"
                    className="filter-input"
                    value={filters.orderId}
                    onChange={(e) => setFilters((prev) => ({ ...prev, orderId: e.target.value }))}
                    placeholder={t('admin.transactions.filters.orderIdPlaceholder')}
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">{t('admin.transactions.filters.type')}</label>
                  <MultiSelect
                    values={filters.types}
                    options={typeOptions}
                    onChange={handleTypesChange}
                    allLabel={t('admin.transactions.filters.allTypes')}
                    summaryLabel={(count) => t('admin.transactions.filters.typesSelected', { count })}
                    minWidth="100%"
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">{t('admin.transactions.filters.createdFrom')}</label>
                  <CustomDatePicker
                    selected={filters.createdFrom ? new Date(filters.createdFrom) : null}
                    onChange={handleDateChange('createdFrom')}
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">{t('admin.transactions.filters.createdTo')}</label>
                  <CustomDatePicker
                    selected={filters.createdTo ? new Date(filters.createdTo) : null}
                    onChange={handleDateChange('createdTo')}
                  />
                </div>
              </div>

              <div className="search-actions">
                <button
                  type="button"
                  onClick={handleReset}
                  className={`search-btn search-btn-reset ${filtersActive ? 'search-btn-reset--active' : ''}`}
                  disabled={loading}
                >
                  <Icon name="x-circle" size={16} />
                  <span className="btn-text">{t('common.reset')}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {!error && renderSummary()}

      <div className="orders-content-surface">
        <div className="orders-cards-wrapper">
          {loading && transactions.length === 0 && (
            <div className="home-logs-loading">
              <div className="strict-loader"></div>
              <span>{t('common.loading')}</span>
            </div>
          )}
          {error && !loading && (
            <div className="form-status error" role="alert">
              {error}
            </div>
          )}
          {!loading && !error && transactions.length === 0 && (
            <div className="admin-promo-empty-state">
              <Icon name="credit-card" size={40} />
              <p>{t('admin.transactions.empty')}</p>
            </div>
          )}
          {!loading && transactions.length > 0 && (
            <>
              <div className="admin-promo-table-wrap admin-promo-table-wrap--list">
                <table className="admin-promo-table admin-promo-table--list admin-mkt-table">
                  <thead>
                    <tr>
                      <th>{t('admin.transactions.table.type')}</th>
                      <th>{t('admin.transactions.table.amount')}</th>
                      <th>{t('admin.transactions.table.balanceAfter')}</th>
                      <th>{t('admin.transactions.table.userProfileId')}</th>
                      <th>{t('admin.transactions.table.orderId')}</th>
                      <th>{t('admin.transactions.table.createdAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => {
                      const direction = directionOf(tx.type);
                      return (
                        <tr key={tx.id}>
                          <td>
                            <span
                              className={`admin-promo-badge admin-mkt-status admin-mkt-status--${
                                direction === 'CREDIT' ? 'closed' : 'open'
                              }`}
                            >
                              <Icon name={direction === 'CREDIT' ? 'trending-up' : 'trending-down'} size={13} />
                              {typeLabel(tx.type)}
                            </span>
                          </td>
                          <td className="admin-mkt-cell-price">
                            {t('admin.transactions.amountValue', { amount: formatAmount(tx.amount, locale) })}
                          </td>
                          <td>{t('admin.transactions.amountValue', { amount: formatAmount(tx.balanceAfter, locale) })}</td>
                          <td>
                            {tx.userProfileId ? (
                              <span
                                className="admin-tx-link-cell"
                                onClick={() => goToUser(tx.userProfileId)}
                                title={t('admin.transactions.table.openUser')}
                              >
                                {tx.userFullName || tx.userProfileId}
                              </span>
                            ) : (
                              <span className="admin-promo-muted">—</span>
                            )}
                          </td>
                          <td>
                            {tx.orderId ? (
                              <span
                                className="admin-tx-link-cell"
                                onClick={() => goToOrder(tx.orderId)}
                                title={t('admin.transactions.table.openOrder')}
                              >
                                {tx.orderId}
                              </span>
                            ) : (
                              <span className="admin-promo-muted">—</span>
                            )}
                          </td>
                          <td className="admin-promo-table-date">
                            {formatDisplayDate(tx.createdAt, locale)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {renderPager()}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminTransactions;
